import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SparklesIcon, CheckIcon, ChevronDownIcon, ImageIcon, GlobeIcon, PlusIcon } from 'lucide-react';
import { ShopConfig, ShopSeoConfig } from '../../data/shopConfig';
import { seoStatus, suggestSeo } from '../../utils/seo';
interface SeoModalProps {
  config: ShopConfig;
  primaryUrl: string;
  onChange: (patch: Partial<ShopSeoConfig>) => void;
  onClose: () => void;
}

/**
 * Search & Sharing — three questions only: how does my shop look on Google,
 * how does my link look when shared, and can Google find me. Sitemaps,
 * metadata, structured data and clean URLs are Talab's job and never appear.
 */
export function SeoModal({
  config,
  primaryUrl,
  onChange,
  onClose
}: SeoModalProps) {
  const seo = config.seo;
  const status = seoStatus(seo);
  const suggestion = useMemo(() => suggestSeo(config), [config]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: seo.title,
    description: seo.description
  });
  /** Talab's proposal, held until the merchant accepts it. */
  const [proposal, setProposal] = useState<null | {
    title: string;
    description: string;
    image: boolean;
  }>(null);
  const [extras, setExtras] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const openEditor = () => {
    setDraft({
      title: seo.title,
      description: seo.description
    });
    setEditing(true);
  };
  return <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4267" data-mp="2t1y2qx17omtj">
      <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-4268" data-mp="2szv3dme65hc5" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full max-w-xl bg-white rounded-3xl shadow-large overflow-hidden max-h-[90vh] flex flex-col" data-id="element-4269" data-mp="2j3rzoc3o5an3">
        <div className="flex items-start gap-4 px-7 pt-6 pb-5 border-b border-slate-100" data-id="element-4270" data-mp="aw58vo2hvkl7">
          <div className="min-w-0 flex-1" data-id="element-4271" data-mp="5wsg6moxzjap">
            <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-4272" data-mp="5xlovt7goivh">
              Search &amp; Sharing
            </h2>
            <p className="text-[13px] text-slate-500 mt-1 leading-relaxed" data-id="element-4273" data-mp="ylzmi7un1d6i">
              Control how your shop appears on Google and when you share its
              link.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-4274" data-mp="ow6ax6uhwy29">
            <XIcon className="w-4 h-4" data-id="element-4275" data-mp="1helg0li2ornx" />
          </button>
        </div>

        <div className="px-7 py-6 overflow-y-auto flex-1" data-id="element-4276" data-mp="1ckz26q06ahqj">
          {/* --------------------------------------- Let Talab do it for you */}
          {!proposal ? <button onClick={() => setProposal({
          title: suggestion.title,
          description: suggestion.description,
          image: !seo.shareImage
        })} className="w-full flex items-start gap-3 p-4 rounded-2xl border border-ai-purple/25 bg-ai-purple/[0.03] text-left hover:bg-ai-purple/[0.06] transition-colors duration-150 ease-out" data-id="element-4277" data-mp="3073gtp41yxkx">
              <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0 mt-0.5" data-id="element-4278" data-mp="3j44k95hykpmr" />
              <span className="min-w-0" data-id="element-4279" data-mp="3nzah2zcwfopm">
                <span className="block text-[13.5px] font-bold text-ai-purple" data-id="element-4280" data-mp="3rzauqhs6bvvm">
                  Optimize automatically
                </span>
                <span className="block text-[12.5px] text-slate-500 mt-0.5 leading-relaxed" data-id="element-4281" data-mp="u18n17aw1ra">
                  Talab can improve your shop title, description and sharing
                  image using your business and catalogue.
                </span>
              </span>
            </button> : <div className="rounded-2xl border border-ai-purple/25 bg-ai-purple/[0.03] p-4" data-id="element-4282" data-mp="3javi7cihf0tu">
              <p className="inline-flex items-center gap-2 text-[13.5px] font-bold text-ai-purple" data-id="element-4283" data-mp="1tvr1586n8rg">
                <SparklesIcon className="w-4 h-4" data-id="element-4284" data-mp="3t1qe78lj5esh" />
                Talab updated your shop
              </p>
              <ul className="mt-3 space-y-1.5" data-id="element-4285" data-mp="35dgo3uhaa4vj">
                {['Google title', 'Search description', proposal.image && 'Sharing image'].filter(Boolean).map(l => <li key={String(l)} className="flex items-center gap-2 text-[13px] text-dark-navy" data-id="element-4286" data-mp="3a8w1klbdpyrf">
                      <CheckIcon className="w-3.5 h-3.5 text-success flex-shrink-0" data-id="element-4287" data-mp="pj6ke1n7e0vk" />
                      {l}
                    </li>)}
              </ul>
              <div className="mt-3 p-3 rounded-xl bg-white border border-slate-200" data-id="element-4288" data-mp="122kdruvttcfl">
                <p className="text-[14px] text-[#1a0dab] leading-snug" data-id="element-4289" data-mp="o32iqxrxt2xs">
                  {proposal.title}
                </p>
                <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed" data-id="element-4290" data-mp="s44nfu4mi775">
                  {proposal.description}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-3" data-id="element-4291" data-mp="1fwl2dvt2dj28">
                <button onClick={() => {
              onChange({
                title: proposal.title,
                description: proposal.description,
                ...(proposal.image ? {
                  shareImage: config.logo
                } : {})
              });
              setProposal(null);
            }} className="px-4 py-2.5 rounded-xl bg-ai-purple text-white text-[13px] font-bold hover:bg-purple-600 transition-colors duration-150 ease-out" data-id="element-4292" data-mp="1ks0fumn5tcy4">
                  Use changes
                </button>
                <button onClick={() => setProposal(null)} className="px-3.5 py-2.5 text-[13px] font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4293" data-mp="24e2arxmcm5ip">
                  Cancel
                </button>
              </div>
            </div>}

          {/* ---------------------------------------------- Google appearance */}
          <section className="mt-7" data-id="element-4294" data-mp="1lp5ddkvnmww9">
            <h3 className="font-display font-bold text-[15px] text-dark-navy" data-id="element-4295" data-mp="1gpj2vj77nur5">
              Google Appearance
            </h3>
            <p className="text-[12.5px] text-slate-500 mt-1 mb-3" data-id="element-4296" data-mp="1bqplvgsptlm8">
              This is how your shop may appear in Google search.
            </p>

            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-id="element-4297" data-mp="16tg0cc8g5u3o">
              <div className="flex items-center gap-2 mb-2" data-id="element-4298" data-mp="wmwlwafr1bjj">
                <span className="w-6 h-6 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-4299" data-mp="11lzqjtrthfqz">
                  {config.logo ? <img src={config.logo} alt="" className="w-full h-full object-contain p-0.5" data-id="element-4300" data-mp="1bl8jcjay4obr" /> : <GlobeIcon className="w-3 h-3 text-slate-300" data-id="element-4301" data-mp="iv8bcbl5ev4o" />}
                </span>
                <span className="min-w-0" data-id="element-4302" data-mp="nu34a6cty02i">
                  <span className="block text-[12.5px] font-semibold text-dark-navy leading-tight" data-id="element-4303" data-mp="nt1hx5qd5r5h">
                    {config.publicName}
                  </span>
                  <span className="block text-[11.5px] text-slate-500 leading-tight" data-id="element-4304" data-mp="3rb1ruqfhc6lq">
                    {primaryUrl}
                  </span>
                </span>
              </div>
              <p className="text-[18px] text-[#1a0dab] leading-snug" data-id="element-4305" data-mp="1eb8gn6cg3jqt">
                {seo.title || `${config.publicName} — ${config.businessType}`}
              </p>
              <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed" data-id="element-4306" data-mp="1oa6ckuc7afju">
                {seo.description || 'Add a short description so customers know what you offer before they click.'}
              </p>
            </div>

            {!editing ? <button onClick={openEditor} className="mt-3 px-4 py-2.5 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-4307" data-mp="1x8rqgc6q96ea">
                Edit Google appearance
              </button> : <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} className="mt-3 rounded-2xl border border-slate-200 p-4" data-id="element-4308" data-mp="2gzn0beovl89q">
                <label className="block" data-id="element-4309" data-mp="2lpqqij02ro6h">
                  <span className="text-[12.5px] font-bold text-dark-navy" data-id="element-4310" data-mp="2qlo5olzlqoho">
                    Shop title
                  </span>
                  <input value={draft.title} onChange={e => setDraft(d => ({
                ...d,
                title: e.target.value
              }))} placeholder="EasyLease | Car Rental & Leasing in UAE" className="w-full mt-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-dark-navy focus:outline-none focus:border-primary-indigo transition-colors duration-150 ease-out" data-id="element-4311" data-mp="2bw9xvibocf4e" />
                </label>
                <label className="block mt-3" data-id="element-4312" data-mp="28iqa49narh5i">
                  <span className="text-[12.5px] font-bold text-dark-navy" data-id="element-4313" data-mp="2dj4jpw4jqz5n">
                    Short description
                  </span>
                  <textarea value={draft.description} onChange={e => setDraft(d => ({
                ...d,
                description: e.target.value
              }))} rows={3} placeholder="Rent SUVs, sedans and luxury vehicles across Dubai and Abu Dhabi." className="w-full mt-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-dark-navy resize-none focus:outline-none focus:border-primary-indigo transition-colors duration-150 ease-out" data-id="element-4314" data-mp="1ynf54j1oxoay" />
                </label>
                <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-4315" data-mp="3f0wz25o2nld3">
                  <button onClick={() => {
                onChange(draft);
                setEditing(false);
              }} className="px-4 py-2.5 rounded-xl bg-primary-indigo text-white text-[13px] font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4316" data-mp="3f27y5s6ko4xv">
                    Save
                  </button>
                  <button onClick={() => setDraft({
                title: suggestion.title,
                description: suggestion.description
              })} className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-ai-purple hover:bg-ai-purple/[0.06] transition-colors duration-150 ease-out" data-id="element-4317" data-mp="kmirgi9g2nw1">
                    <SparklesIcon className="w-3.5 h-3.5" data-id="element-4318" data-mp="250svt1gfovw1" />
                    Improve with Talab
                  </button>
                  <button onClick={() => setEditing(false)} className="px-3 py-2.5 text-[13px] font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4319" data-mp="2134t3c8i1ytl">
                    Cancel
                  </button>
                </div>
              </motion.div>}
          </section>

          {/* ------------------------------------------------ Shared link */}
          <section className="mt-8 pt-7 border-t border-slate-100" data-id="element-4320" data-mp="f0oet68nwivt">
            <h3 className="font-display font-bold text-[15px] text-dark-navy" data-id="element-4321" data-mp="jxyfxj5eyizh">
              Shared Link Appearance
            </h3>
            <p className="text-[12.5px] text-slate-500 mt-1 mb-3" data-id="element-4322" data-mp="nz9y53lyk1xn">
              This is how your shop looks when shared on WhatsApp or social
              media.
            </p>

            <div className="max-w-sm rounded-2xl border border-slate-200 overflow-hidden bg-white" data-id="element-4323" data-mp="3sd8c6ammgtzc">
              <div className="h-36 bg-slate-50 flex items-center justify-center" data-id="element-4324" data-mp="9fk4ipwk7bt">
                {seo.shareImage ? <img src={seo.shareImage} alt="" className="w-full h-full object-contain p-8" data-id="element-4325" data-mp="3ovu3pks4x6nj" /> : <span className="inline-flex flex-col items-center gap-1.5 text-slate-300" data-id="element-4326" data-mp="3fu4ac9eozxn4">
                    <ImageIcon className="w-6 h-6" data-id="element-4327" data-mp="3avt4e9riq7a6" />
                    <span className="text-[11.5px] font-semibold" data-id="element-4328" data-mp="35x81g2kvt9i1">
                      No image yet
                    </span>
                  </span>}
              </div>
              <div className="p-4 border-t border-slate-100" data-id="element-4329" data-mp="2w7oh0shit7l2">
                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold" data-id="element-4330" data-mp="2w87x03nm1lwg">
                  {primaryUrl}
                </p>
                <p className="text-[14px] font-bold text-dark-navy mt-1" data-id="element-4331" data-mp="2n6qnx58mekh9">
                  {seo.title || config.publicName}
                </p>
                <p className="text-[12.5px] text-slate-500 mt-0.5 line-clamp-2" data-id="element-4332" data-mp="2i98dtbiv9z7o">
                  {seo.description || config.about}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-4333" data-mp="r2k86jfhhl7k">
              <label className="px-4 py-2.5 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-700 hover:border-primary-indigo hover:text-primary-indigo cursor-pointer transition-colors duration-150 ease-out" data-id="element-4334" data-mp="cfhbug2wdilt">
                Change image
                <input type="file" accept="image/*" className="hidden" onChange={e => {
                const f = e.target.files?.[0];
                if (f) onChange({
                  shareImage: URL.createObjectURL(f)
                });
              }} data-id="element-4335" data-mp="3czqs4yzaw0e" />
              </label>
              <button onClick={() => onChange({
              shareImage: config.logo
            })} className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-[13px] font-bold text-ai-purple hover:bg-ai-purple/[0.06] transition-colors duration-150 ease-out" data-id="element-4336" data-mp="1nsbgblgp76vu">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-4337" data-mp="3n2e0el18q8fq" />
                Create from my shop
              </button>
            </div>
            <p className="text-[12px] text-slate-400 mt-2.5" data-id="element-4338" data-mp="3w1rcllgr1b45">
              The title and description above are used automatically.
            </p>
          </section>

          {/* ------------------------------------------------- Visibility */}
          <section className="mt-8 pt-7 border-t border-slate-100" data-id="element-4339" data-mp="9t884wnbwknc">
            <div className="flex items-start justify-between gap-4" data-id="element-4340" data-mp="4uwyalx1aa54">
              <div className="min-w-0" data-id="element-4341" data-mp="4tluim3l92e0">
                <p className="font-display font-bold text-[15px] text-dark-navy" data-id="element-4342" data-mp="2bl4ja55l3shl">
                  Show my shop on Google
                </p>
                <p className="text-[12.5px] text-slate-500 mt-1 leading-relaxed" data-id="element-4343" data-mp="26ontl6iql62e">
                  When on, Talab allows search engines to discover your shop,
                  categories and products.
                </p>
              </div>
              <button role="switch" aria-checked={seo.indexable} onClick={() => onChange({
              indexable: !seo.indexable
            })} className={`w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-150 ease-out ${seo.indexable ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-4344" data-mp="35b3vz0ypluqs">
                <span className={`block w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-150 ease-out ${seo.indexable ? 'translate-x-[22px]' : 'translate-x-0.5'}`} data-id="element-4345" data-mp="3o3641nxac2pf" />
              </button>
            </div>

            {/* Genuinely optional, and collapsed so it never reads as a task */}
            <button onClick={() => setExtras(v => !v)} className="inline-flex items-center gap-1.5 mt-5 text-[12.5px] font-semibold text-slate-400 hover:text-slate-600 transition-colors duration-150 ease-out" data-id="element-4346" data-mp="6rx0pc0m9cca">
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-150 ease-out ${extras ? 'rotate-180' : ''}`} data-id="element-4347" data-mp="1hexfia88qix1" />
              Optional · What else should customers find you for?
            </button>
            {extras && <div className="mt-3" data-id="element-4348" data-mp="253qbg6ov4qrn">
                <p className="text-[12px] text-slate-400 mb-2.5" data-id="element-4349" data-mp="254rp8ix9jrd2">
                  Talab already uses your business type, categories, products
                  and location. Add anything extra here.
                </p>
                <div className="flex flex-wrap items-center gap-2" data-id="element-4350" data-mp="1vddn03dxgdg8">
                  {seo.topics.map(t => <span key={t} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-[13px] font-semibold text-slate-700" data-id="element-4351" data-mp="2suvv6wxxc378">
                      {t}
                      <button onClick={() => onChange({
                  topics: seo.topics.filter(x => x !== t)
                })} aria-label={`Remove ${t}`} className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:bg-white hover:text-error transition-colors duration-150 ease-out" data-id="element-4352" data-mp="216rf1zpbg9zg">
                        <XIcon className="w-3 h-3" data-id="element-4353" data-mp="2641bhzb4hlws" />
                      </button>
                    </span>)}
                  <input value={newTopic} onChange={e => setNewTopic(e.target.value)} onKeyDown={e => {
                if (e.key !== 'Enter') return;
                const v = newTopic.trim();
                if (!v || seo.topics.includes(v)) return;
                onChange({
                  topics: [...seo.topics, v]
                });
                setNewTopic('');
              }} placeholder="Monthly car rental" className="w-48 px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-[13px] focus:outline-none focus:border-primary-indigo transition-colors duration-150 ease-out" data-id="element-4354" data-mp="2b5gxbtl4jwg8" />
                  {newTopic.trim() && <button onClick={() => {
                const v = newTopic.trim();
                if (seo.topics.includes(v)) return;
                onChange({
                  topics: [...seo.topics, v]
                });
                setNewTopic('');
              }} aria-label="Add" className="w-7 h-7 rounded-full bg-primary-indigo text-white flex items-center justify-center" data-id="element-4355" data-mp="i6s81bqgcrse">
                      <PlusIcon className="w-3.5 h-3.5" data-id="element-4356" data-mp="28ekjhfpxo0nq" />
                    </button>}
                </div>
              </div>}
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 px-7 py-5 border-t border-slate-100" data-id="element-4357" data-mp="2ibfkq6dlcf5p">
          <p className="text-[13px] font-semibold min-w-0" data-id="element-4358" data-mp="2w22hhszsjuz5">
            {status.ready ? <span className="inline-flex items-center gap-1.5 text-success" data-id="element-4359" data-mp="2s2a3v1vjiymx">
                <CheckIcon className="w-4 h-4" data-id="element-4360" data-mp="3611f8gj0q67d" />
                Your shop is ready for search
              </span> : <span className="text-slate-500" data-id="element-4361" data-mp="313i25jy23zh0">{status.blocking[0]}</span>}
            {status.ready && status.optional.length > 0 && <span className="block text-[12px] text-slate-400 font-normal mt-0.5" data-id="element-4362" data-mp="16o5ps7j9eqcm">
                Optional: {status.optional.join(' · ')}
              </span>}
          </p>
          <button onClick={onClose} className="px-5 py-2.5 bg-dark-navy text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-4363" data-mp="3idve1df1xqk0">
            Done
          </button>
        </div>
      </motion.div>
    </div>;
}